import { getApiBaseUrlByTenantCode } from '@tectonics/commons/tenant';
import type { TenantCode } from '@tectonics/commons/tenant';
import type { RiskSignals } from '@tectonics/features/jornada-sem-redirecionamento/pix-enrollment/hooks/use-risk-signals';
import type { AssertionOptions } from '../types';
import type { AssertionResponse } from './webauthn';

interface QueryResponse {
  readonly status: string;
  readonly assertion_options?: AssertionOptions;
  readonly redirect_url_after_payment?: string;
  readonly status_message?: string;
}

interface AuthenticationResponse {
  readonly clientDataJSON: string;
  readonly authenticatorData: string;
  readonly signature: string;
  readonly userHandle: string;
}

interface AuthenticationPayload {
  readonly credentialId: string;
  readonly rawCredentialId: string;
  readonly credentialType: string;
  readonly response: AuthenticationResponse;
}

interface CompletePayload {
  readonly hash: string;
  readonly authentication: AuthenticationPayload;
  readonly risk_signals: RiskSignals;
}

interface CompleteResponse {
  readonly status: string;
  readonly payment?: { readonly hash: string };
  readonly status_code?: string;
  readonly status_message?: string;
}

function getBaseUrl(tenantCode: TenantCode): string {
  return getApiBaseUrlByTenantCode(tenantCode);
}

export async function queryAssertionOptions(
  paymentHash: string,
  tenantCode: TenantCode,
): Promise<QueryResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-payment/query?hash=${encodeURIComponent(paymentHash)}`,
  );

  const data: QueryResponse = await response.json();

  if (!response.ok) {
    throw new Error(data.status_message || 'Falha ao consultar pagamento.');
  }

  return data;
}

export async function completePayment(
  paymentHash: string,
  assertionData: AssertionResponse,
  riskSignals: RiskSignals,
  tenantCode: TenantCode,
): Promise<CompleteResponse> {
  const baseUrl = getBaseUrl(tenantCode);

  const payload: CompletePayload = {
    hash: paymentHash,
    authentication: {
      credentialId: assertionData.credentialId,
      rawCredentialId: assertionData.rawCredentialId,
      credentialType: assertionData.credentialType,
      response: assertionData.response,
    },
    risk_signals: riskSignals,
  };

  const response = await fetch(
    `${baseUrl}cf/hosted-payment/complete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data: CompleteResponse = await response.json();

  if (!response.ok) {
    throw new Error(data.status_message || 'Falha na autenticação do pagamento.');
  }

  return data;
}
